<template>
  <div class="goods-spec-editor">
    <div class="editor-header">
      <div class="header-text">
        <div class="title">商品规格</div>
        <div class="desc">不配置则按无规格商品处理；配置后用户每组规格选择一个，默认取第一项。</div>
      </div>
      <el-button type="primary" plain size="small" @click="addGroup">新增规格组</el-button>
    </div>

    <div v-if="specs.length === 0" class="empty-tip">暂未配置规格</div>

    <div v-for="(group, groupIndex) in specs" :key="groupIndex" class="spec-group">
      <div class="group-header">
        <el-input
          :model-value="group.name"
          placeholder="请输入规格组名称，如：甜度"
          @update:model-value="updateGroupName(groupIndex, $event)"
        />
        <el-button type="danger" plain size="small" @click="removeGroup(groupIndex)">删除组</el-button>
      </div>

      <div class="options-list">
        <div v-for="(option, optionIndex) in group.options" :key="`${groupIndex}-${optionIndex}`" class="option-item">
          <el-input
            :model-value="option"
            placeholder="请输入规格值，如：半糖"
            @update:model-value="updateOption(groupIndex, optionIndex, $event)"
          />
          <el-button type="danger" text @click="removeOption(groupIndex, optionIndex)">删除</el-button>
        </div>
      </div>

      <el-button size="small" plain @click="addOption(groupIndex)">新增选项</el-button>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed } from 'vue';

interface GoodsSpec {
  name: string;
  options: string[];
}

const props = defineProps<{
  modelValue: GoodsSpec[];
}>();

// eslint-disable-next-line func-call-spacing
const emit = defineEmits<{
  (e: 'update:modelValue', value: GoodsSpec[]): void;
}>();

const specs = computed(() => props.modelValue || []);

const cloneSpecs = () => specs.value.map((item) => ({
  name: item.name || '',
  options: [...(item.options || [])],
}));

const updateSpecs = (nextSpecs: GoodsSpec[]) => {
  emit('update:modelValue', nextSpecs);
};

const addGroup = () => {
  const nextSpecs = cloneSpecs();
  nextSpecs.push({
    name: '',
    options: [''],
  });
  updateSpecs(nextSpecs);
};

const removeGroup = (groupIndex: number) => {
  const nextSpecs = cloneSpecs();
  nextSpecs.splice(groupIndex, 1);
  updateSpecs(nextSpecs);
};

const updateGroupName = (groupIndex: number, value: string) => {
  const nextSpecs = cloneSpecs();
  nextSpecs[groupIndex].name = value;
  updateSpecs(nextSpecs);
};

const addOption = (groupIndex: number) => {
  const nextSpecs = cloneSpecs();
  nextSpecs[groupIndex].options.push('');
  updateSpecs(nextSpecs);
};

const updateOption = (groupIndex: number, optionIndex: number, value: string) => {
  const nextSpecs = cloneSpecs();
  nextSpecs[groupIndex].options[optionIndex] = value;
  updateSpecs(nextSpecs);
};

const removeOption = (groupIndex: number, optionIndex: number) => {
  const nextSpecs = cloneSpecs();
  nextSpecs[groupIndex].options.splice(optionIndex, 1);
  if (nextSpecs[groupIndex].options.length === 0) {
    nextSpecs[groupIndex].options.push('');
  }
  updateSpecs(nextSpecs);
};
</script>

<style scoped lang="scss">
.goods-spec-editor {
  display: flex;
  flex-direction: column;
  gap: 12px;

  .editor-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 12px;

    .header-text {
      .title {
        font-size: 14px;
        font-weight: 600;
        color: #303133;
      }

      .desc {
        margin-top: 4px;
        font-size: 12px;
        color: #909399;
        line-height: 1.4;
      }
    }
  }

  .empty-tip {
    padding: 12px;
    border: 1px dashed #dcdfe6;
    border-radius: 8px;
    color: #909399;
    font-size: 12px;
  }

  .spec-group {
    padding: 12px;
    border: 1px solid #ebeef5;
    border-radius: 8px;
    background: #fafafa;

    .group-header {
      display: flex;
      align-items: center;
      gap: 8px;
      margin-bottom: 10px;
    }

    .options-list {
      display: flex;
      flex-direction: column;
      gap: 8px;
      margin-bottom: 10px;

      .option-item {
        display: flex;
        align-items: center;
        gap: 8px;
      }
    }
  }
}
</style>
